import type { CreateCustomerContactInput, UpdateCustomerContactInput } from '@chukua/common';
import { Button, Popconfirm } from 'antd';
import { Pencil, Plus, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../../../shared/components/EmptyState';
import { CustomerContactModal } from './CustomerContactModal';
import type { CustomerContact } from '../customers.api';

interface CustomerContactsProps {
  contacts: CustomerContact[];
  canManage: boolean;
  onAdd: (input: CreateCustomerContactInput) => Promise<void>;
  onUpdate: (contactId: string, input: UpdateCustomerContactInput) => Promise<void>;
  onRemove: (contactId: string, name: string) => Promise<void>;
}

/** Who to reach about this customer's billing — add/edit/remove are ordinary edits, not evidence (CLAUDE.md's append-only rule doesn't apply). */
export function CustomerContacts({ contacts, canManage, onAdd, onUpdate, onRemove }: CustomerContactsProps) {
  const [editing, setEditing] = useState<CustomerContact | 'new' | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function submit(input: CreateCustomerContactInput) {
    setSubmitting(true);
    try {
      if (editing === 'new') await onAdd(input);
      else if (editing) await onUpdate(editing.id, input);
      setEditing(null);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-fg">Contacts</h2>
        {canManage && (
          <Button size="small" icon={<Plus className="size-3.5" />} onClick={() => setEditing('new')}>
            Add contact
          </Button>
        )}
      </div>
      {contacts.length === 0 && <EmptyState icon={UserRound} title="No contacts yet" description="Add the people to reach about this customer's billing." />}
      {contacts.length > 0 && (
        <ul className="flex flex-col gap-3">
          {contacts.map((contact) => (
            <ContactRow key={contact.id} contact={contact} canManage={canManage} onEdit={() => setEditing(contact)} onRemove={onRemove} />
          ))}
        </ul>
      )}
      <CustomerContactModal
        open={editing !== null}
        contact={editing === 'new' ? null : editing}
        submitting={submitting}
        onSubmit={submit}
        onCancel={() => setEditing(null)}
      />
    </section>
  );
}

function ContactRow({
  contact,
  canManage,
  onEdit,
  onRemove,
}: {
  contact: CustomerContact;
  canManage: boolean;
  onEdit: () => void;
  onRemove: (contactId: string, name: string) => Promise<void>;
}) {
  return (
    <li className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div>
        <p className="font-medium text-fg">
          {contact.name}
          {contact.role && <span className="ml-2 text-sm text-muted">{contact.role}</span>}
        </p>
        <p className="text-sm text-muted">{[contact.phone, contact.email].filter(Boolean).join(' · ') || '—'}</p>
      </div>
      {canManage && (
        <div className="flex items-center gap-1">
          <Button type="text" size="small" icon={<Pencil className="size-3.5" />} onClick={onEdit} aria-label={`Edit ${contact.name}`} />
          <Popconfirm title={`Remove ${contact.name}?`} onConfirm={() => onRemove(contact.id, contact.name)}>
            <Button type="text" size="small" danger icon={<Trash2 className="size-3.5" />} aria-label={`Remove ${contact.name}`} />
          </Popconfirm>
        </div>
      )}
    </li>
  );
}
